// external imports
import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Chip,
  Checkbox,
  Radio,
  Tabs,
  Tab,
} from '@mui/material';
import { Search, PersonAddAlt1, SwapHoriz } from '@mui/icons-material';

// avatar color palette for target cluster-manager cards (matches the left list)
const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#c2410c', '#be185d', '#4338ca'];

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();

/**
 * A single dialog component that renders one of three flows based on `mode`:
 *  - "add"      → search employees / unassigned trainers and add them under the CM
 *  - "reassign" → move ALL of the CM's trainers to another CM
 *  - "move"     → move the currently selected trainers to another CM
 *
 * All state that matters to the parent is surfaced through onConfirm/onClose.
 */
const ClusterModals = ({
  mode, // 'add' | 'reassign' | 'move' | null
  open,
  manager, // the active cluster manager
  managers = [], // all cluster managers (for the target picker)
  employees = [], // all employees (for the add flow)
  unassignedTrainers = [], // trainers not yet in a cluster (for the add flow)
  selectedTrainerCount = 0, // number of trainers selected in the table (move flow)
  submitting = false, // true while the confirm request is in flight
  onClose,
  onConfirm,
}) => {
  const isAdd = mode === 'add';
  const isReassign = mode === 'reassign';
  const isMove = mode === 'move';

  // ---- add-flow state ----
  const [tab, setTab] = React.useState(0); // 0 = all employees, 1 = unassigned trainers
  const [search, setSearch] = React.useState('');
  const [checkedEmployees, setCheckedEmployees] = React.useState([]);

  // ---- reassign / move flow state ----
  const [targetCM, setTargetCM] = React.useState(null);

  // reset local state each time the dialog opens or the mode changes
  React.useEffect(() => {
    if (open) {
      setTab(0);
      setSearch('');
      setCheckedEmployees([]);
      setTargetCM(null);
    }
  }, [open, mode]);

  const paperSx = {
    borderRadius: '16px',
    width: '100%',
    maxWidth: isAdd ? 620 : 560,
  };

  // ---------------------------------------------------------------- ADD FLOW
  const renderAdd = () => {
    const source = unassignedTrainers?.length ? unassignedTrainers : employees;
    const query = search.trim().toLowerCase();
    const list = query
      ? source.filter(
          (e) => e.fullName.toLowerCase().includes(query) || e.empId.toLowerCase().includes(query)
        )
      : source;

    const toggle = (empId) =>
      setCheckedEmployees((prev) =>
        prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
      );

    return (
      <>
        {/* header */}
        <Box sx={{ p: 3, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <PersonAddAlt1 sx={{ color: '#2563eb' }} />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
              {`Add trainers under ${manager?.fullName ?? ''}`}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, color: '#64748b', mt: 0.5 }}>
            Search any employee to promote, or pick from trainers not yet in a cluster.
          </Typography>
        </Box>

        {/* search */}
        <Box sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder={tab === 0 ? 'Search employee by name or ID...' : 'Search trainers...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: { borderRadius: '12px', background: '#f8fafc' },
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#2563eb' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* list */}
        <Box sx={{ maxHeight: 340, overflowY: 'auto', borderTop: '1px solid #f1f5f9' }}>
          {list.map((emp) => {
            const isChecked = checkedEmployees.includes(emp.empId);
            return (
              <Box
                key={emp.empId}
                onClick={() => toggle(emp.empId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f5f9',
                  background: isChecked ? '#eff6ff' : 'transparent',
                  '&:hover': { background: isChecked ? '#eff6ff' : '#f8fafc' },
                }}
              >
                <Checkbox size="small" checked={isChecked} sx={{ p: 0.5 }} />
                <Avatar
                  sx={{ bgcolor: '#64748b', width: 40, height: 40, fontSize: 14, fontWeight: 600 }}
                >
                  {getInitials(emp.fullName)}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, color: '#0f172a' }}>
                    <b>{emp.fullName}</b>
                    <span style={{ color: '#94a3b8', fontWeight: 400 }}>{`  ·  ${emp.empId}`}</span>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                    {[emp.designation, emp.location].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Chip
                  label={emp.tag || (tab === 0 ? 'Employee' : 'Trainer')}
                  size="small"
                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: 12.5 }}
                />
              </Box>
            );
          })}

          {list.length === 0 && (
            <Typography sx={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', py: 4 }}>
              No results found.
            </Typography>
          )}
        </Box>

        {/* footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 3,
            py: 2,
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <Typography sx={{ fontSize: 14, color: '#334155' }}>
            <b>{checkedEmployees.length}</b> selected to add
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={checkedEmployees.length === 0 || submitting}
              onClick={() => onConfirm?.({ mode, employeeIds: checkedEmployees })}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              {submitting ? 'Adding…' : `Add ${checkedEmployees.length} trainer(s)`}
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  // ------------------------------------------------------ REASSIGN / MOVE FLOW
  const renderTargetPicker = () => {
    const targets = managers.filter((m) => m.empId !== manager?.empId);
    const count = isReassign ? (manager?.trainers?.length ?? 0) : selectedTrainerCount;
    const targetName = targets.find((m) => m.empId === targetCM)?.fullName ?? '—';

    return (
      <>
        {/* header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <SwapHoriz sx={{ color: '#2563eb' }} />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
              {isReassign ? 'Reassign entire cluster' : 'Move selected trainers'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, color: '#64748b', mt: 0.5 }}>
            {isReassign
              ? `Move all trainers currently under ${manager?.fullName ?? ''} to a different cluster manager.`
              : `Move the ${count} selected trainer(s) to a different cluster manager.`}
          </Typography>
        </Box>

        {/* target list */}
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.05em',
              color: '#94a3b8',
              mb: 1.5,
            }}
          >
            SELECT TARGET CLUSTER MANAGER
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {targets.map((m, index) => {
              const selected = m.empId === targetCM;
              return (
                <Box
                  key={m.empId}
                  onClick={() => setTargetCM(m.empId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    border: selected ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                    background: selected ? '#eff6ff' : '#ffffff',
                    '&:hover': { borderColor: selected ? '#93c5fd' : '#cbd5e1' },
                  }}
                >
                  <Radio size="small" checked={selected} sx={{ p: 0.5 }} />
                  <Avatar
                    sx={{
                      bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length],
                      width: 44,
                      height: 44,
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(m.fullName)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, color: '#0f172a' }}>
                      <b>{m.fullName}</b>
                      <span style={{ color: '#94a3b8', fontWeight: 400 }}>{`  ·  ${m.empId}`}</span>
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                      {`${m.regionsLabel} · ${m.trainers?.length ?? m.trainerCount ?? 0} trainers`}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 3,
            py: 2,
            mt: 1,
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <Typography sx={{ fontSize: 14, color: '#334155' }}>
            <b>{count}</b>
            {` trainer(s) will move to ${targetName}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!targetCM || submitting}
              onClick={() => onConfirm?.({ mode, targetCMId: targetCM })}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              {submitting ? 'Moving…' : 'Confirm Move'}
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Dialog open={!!open} onClose={onClose} PaperProps={{ sx: paperSx }}>
      {isAdd ? renderAdd() : renderTargetPicker()}
    </Dialog>
  );
};

export default ClusterModals;
